import type { TvCategoryEx } from './index.h'

import { A, pipe } from '@mobily/ts-belt'
import { api } from '@setplex/tria-api'
import { attach, createEvent, createStore, sample } from 'effector'
import { not } from 'patronum'
import { concatById, sortBySortOrder } from '~/shared/helpers'
import { CategoryId } from './index.h'
import {
  hasChannels,
  idByType,
  isNotType,
  toAllType,
  toFavoriteType,
  toFeaturedType,
  toRegularType,
} from './lib'

// all loaded categories
export const $loaded = createStore<TvCategoryEx[]>([])

// flags that indicates if there are more categories to load
export const $isLast = createStore(false)
export const $hasMore = not($isLast)

// made-up categories (like "All", "Favorites", and "Featured")
export const $madeup = createStore<TvCategoryEx[]>([])

// add/replace made-up category
export const addMadeupCategory = createEvent<TvCategoryEx>()

// add "Favorite" made-up category
export const addFavoriteCategory = addMadeupCategory.prepend<readonly number[]>(
  (channelIds) => toFavoriteType({ channelIds })
)

// add "All" made-up category
export const addAllCategory = addMadeupCategory.prepend<readonly number[]>(
  (channelIds) => toAllType({ channelIds })
)

// add "Featured" made-up category
export const addFeaturedCategory = addMadeupCategory.prepend<readonly number[]>(
  (channelIds) => toFeaturedType({ channelIds })
)

// remove made-up category
export const removeMadeupCategory = createEvent<number>()

// remove "Favorite" made-up category
export const removeFavoriteCategory = removeMadeupCategory.prepend(
  () => CategoryId.Favorite
)

// load categories by page
export const loadPageFx = attach({
  effect: api.tv.getTvCategoriesByPageAbortableFx,
})

// when page is loaded - concat new categories to the already loaded categories
sample({
  clock: loadPageFx.doneData,
  source: $loaded,
  fn: (loaded, { content }) =>
    pipe(
      [...content],
      A.filter(hasChannels),
      A.map(toRegularType),
      concatById(loaded),
      sortBySortOrder
    ),
  target: $loaded,
})

// when page is loaded - check if there are more categories to load
sample({
  clock: loadPageFx.doneData,
  fn: ({ last }) => Boolean(last),
  target: $isLast,
})

// add any made-up category
sample({
  clock: addMadeupCategory,
  source: $madeup,
  filter: (_, { channelIds }) => channelIds.length > 0,
  fn: (madeup, { type, channelIds }) =>
    pipe(
      madeup,
      A.filter(isNotType(type)),
      A.append({
        id: idByType(type),
        sortOrder: type,
        channelIds,
        type,
      } as TvCategoryEx),
      sortBySortOrder
    ),
  target: $madeup,
})

// remove any made-up category
sample({
  clock: removeMadeupCategory,
  source: $madeup,
  fn: (madeup, toRemoveId) => A.reject(madeup, ({ id }) => id === toRemoveId),
  target: $madeup,
})
